import { forwardRef, cloneElement, isValidElement } from "react";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import { Icon, Tooltip, Box, Typography } from "@mui/material";
import { useMaterialUIController } from "context";

const UCNotificationItem = forwardRef(
  ({ icon, title, badgeContent, demoContent, ...rest }, ref) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const textColor = darkMode ? "#ffffff" : "rgba(0,0,0,0.87)";

    // Clone the icon and force color to match text
    const styledIcon = isValidElement(icon)
      ? cloneElement(icon, {
          sx: { ...((icon.props && icon.props.sx) || {}), color: `${textColor} !important` },
          style: { ...((icon.props && icon.props.style) || {}), color: textColor },
        })
      : icon;

    return (
      <MenuItem
        {...rest}
        ref={ref}
        sx={{
          borderRadius: 1,
          py: 0.75,
          px: 2,
          gap: 1.5,
          color: `${textColor} !important`,
          opacity: "1 !important",
          "&:hover": {
            backgroundColor: darkMode
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.04)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", color: textColor }}>
          {styledIcon}
        </Box>
        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={{ color: `${textColor} !important`, flex: 1 }}
        >
          {title}
        </Typography>

        {demoContent && (
          <Tooltip title="Available in demo mode" placement="top">
            <Icon sx={{ fontSize: 18, color: "info.main", ml: 1 }}>
              visibility
            </Icon>
          </Tooltip>
        )}

        {badgeContent && (
          <Tooltip
            title="New updates are now available for testing"
            placement="top"
          >
            <Icon
              sx={{
                fontSize: 18,
                color: "error.main",
                ml: 1,
                animation: "pulseZoom 1.8s infinite ease-in-out",
                "@keyframes pulseZoom": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.25)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            >
              bug_report
            </Icon>
          </Tooltip>
        )}
      </MenuItem>
    );
  }
);

UCNotificationItem.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  badgeContent: PropTypes.bool,
  demoContent: PropTypes.bool,
};

export default UCNotificationItem;
